<template>
    <section class="personnel-access pdlr-3" :aria-label="titre">
        <div class="personnel-access-heading">
            <div>
                <h3>{{ titre }}</h3>
                <p>Réglages de {{ personnel.name || personnel.nom }}.</p>
            </div>
            <button v-if="!chargement && prestations.length" type="button" class="btn btn-primary-o btn-radius"
                @click="basculerTout">{{ toutSelectionne ? 'Tout retirer' : 'Tout sélectionner' }}</button>
        </div>

        <p v-if="accesAutomatique" class="personnel-access-auto" role="status">
            Accès automatique à tous les examens grâce au rôle imagerie. Les autorisations manuelles ci-dessous
            seront conservées si ce rôle est retiré.
        </p>
        <p v-if="erreur" class="personnel-access-error" role="alert">{{ erreur }}</p>
        <p v-if="succes" class="personnel-access-success" role="status">{{ succes }}</p>
        <div v-if="chargement" class="personnel-access-state" role="status">Chargement des accès…</div>
        <template v-else>
            <p class="personnel-access-instruction">{{ accesAutomatique ? 'Autorisations manuelles conservées' : instruction }}</p>
            <input v-if="prestations.length" v-model="recherche" type="search" class="input-secondary-light personnel-access-search"
                :placeholder="dataType === 'type_examen' ? 'Rechercher un examen' : 'Rechercher une consultation'"
                :aria-label="dataType === 'type_examen' ? 'Rechercher un examen' : 'Rechercher une consultation'">
            <div v-if="prestationsAffichees.length" class="personnel-access-list">
                <label v-for="item in prestationsAffichees" :key="item.id" class="personnel-access-item">
                    <input v-model="selection" type="checkbox" :value="Number(item.id)">
                    <span>{{ libelle(item) }}</span>
                </label>
            </div>
            <p v-else-if="prestations.length" class="personnel-access-state">Aucun résultat pour « {{ recherche }} ».</p>
            <p v-else class="personnel-access-state">Aucune prestation disponible pour ce réglage.</p>
            <div class="personnel-access-actions">
                <button type="button" class="btn btn-primary btn-radius" :disabled="enregistrement || !prestations.length"
                    @click="enregistrer">{{ enregistrement ? 'Enregistrement…' : 'Enregistrer les accès' }}</button>
            </div>
        </template>
    </section>
</template>

<script>
import apiAccess from '@/api/parametres/_accessibilite'
import apiParametres from '@/api/parametres/_parametres'
import apiExamen from '@/api/_examen'

const ROLES_IMAGERIE = ['ROLE_TECHNICIEN_IMAGERIE', 'ROLE_RADIOLOGUE']

export default {
    name: 'AccesPrestations',
    props: {
        personnel: {type: Object, required: true},
        dataType: {type: String, required: true},
    },
    data() {
        return {
            prestations: [], selection: [], chargement: false, enregistrement: false,
            erreur: '', succes: '', requete: 0, recherche: '',
        }
    },
    computed: {
        titre() { return this.dataType === 'type_examen' ? 'Accès examens' : 'Accès consultations' },
        instruction() { return this.dataType === 'type_examen' ? 'Choisir les examens autorisés' : 'Choisir les consultations autorisées' },
        accesAutomatique() {
            return this.dataType === 'type_examen' && Array.isArray(this.personnel.roles) &&
                this.personnel.roles.some(role => ROLES_IMAGERIE.indexOf(role.nom) !== -1)
        },
        // Recherche sans tenir compte des accents ni des majuscules (« echo » trouve « Échographie »).
        prestationsAffichees() {
            const normaliser = texte => String(texte).toLocaleLowerCase('fr-FR').normalize('NFD').replace(/[̀-ͯ]/g, '')
            const recherche = normaliser(this.recherche.trim())
            return recherche ? this.prestations.filter(item => normaliser(this.libelle(item)).indexOf(recherche) !== -1) : this.prestations
        },
        // « Tout sélectionner » porte sur les résultats affichés.
        toutSelectionne() {
            return this.prestationsAffichees.length > 0 && this.prestationsAffichees.every(item => this.selection.indexOf(Number(item.id)) !== -1)
        },
    },
    watch: {
        personnel: {immediate: true, handler() { this.charger() }},
        dataType() { this.charger() },
    },
    beforeDestroy() { this.requete++ },
    methods: {
        libelle(item) { return item.libelle || item.nom || 'Prestation n° ' + item.id },
        async charger() {
            const identifiant = Number(this.personnel && this.personnel.id)
            const type = this.dataType
            const requete = ++this.requete
            this.prestations = []; this.selection = []; this.erreur = ''; this.succes = ''; this.recherche = ''
            this.chargement = false; this.enregistrement = false
            if (!identifiant || ['type_consultation', 'type_examen'].indexOf(type) === -1) return
            this.chargement = true
            try {
                const catalogue = type === 'type_examen' ? apiExamen.getExamens() : apiParametres.getTousTypeConsultations()
                const resultat = await Promise.all([
                    catalogue,
                    apiAccess.getAccessCheckerToTypePrestation({auth_id: identifiant, data_type: type}),
                ])
                if (requete !== this.requete || Number(this.personnel.id) !== identifiant) return
                this.prestations = Array.isArray(resultat[0]) ? resultat[0] : []
                this.selection = Array.isArray(resultat[1]) ? resultat[1].map(Number) : []
            } catch (error) {
                if (requete === this.requete) this.erreur = 'Impossible de charger les accès. Réessayez en rouvrant cet onglet.'
            } finally {
                if (requete === this.requete) this.chargement = false
            }
        },
        basculerTout() {
            const affiches = this.prestationsAffichees.map(item => Number(item.id))
            this.selection = this.toutSelectionne
                ? this.selection.filter(id => affiches.indexOf(Number(id)) === -1)
                : this.selection.concat(affiches.filter(id => this.selection.indexOf(id) === -1))
            this.succes = ''
        },
        async enregistrer() {
            const identifiant = Number(this.personnel.id)
            const type = this.dataType
            const requete = this.requete
            const possibles = this.prestations.map(item => Number(item.id))
            const droits = this.selection.map(Number).filter(id => possibles.indexOf(id) !== -1)
            this.erreur = ''; this.succes = ''; this.enregistrement = true
            try {
                const resultat = await apiAccess.saveConsultationAccessibilite({
                    user_id: identifiant, data_type: type, accessibilites: droits,
                })
                if (requete !== this.requete || Number(this.personnel.id) !== identifiant) return
                if (typeof resultat !== 'number' || resultat < 0) throw new Error('Sauvegarde refusée')
                this.selection = droits
                this.succes = 'Accès enregistrés.'
            } catch (error) {
                if (requete === this.requete) this.erreur = 'Les accès n’ont pas été enregistrés. Réessayez.'
            } finally {
                if (requete === this.requete) this.enregistrement = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.personnel-access { color: var(--ui-text, #344164); }
.personnel-access-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 18px; }
.personnel-access-heading h3 { margin: 0 0 4px; font-size: 20px; font-weight: 700; }
.personnel-access-heading p, .personnel-access-instruction { margin: 0; color: var(--ui-text-secondary, #71809e); font-size: 13px; }
.personnel-access-instruction { margin: 0 0 12px; }
.personnel-access-search { width: 100%; margin-bottom: 12px; }
.personnel-access-auto { border-left: 3px solid #3972ff; background: #eef3ff; padding: 10px 13px; color: var(--ui-text, #344164); font-size: 13px; line-height: 1.5; }
.personnel-access-error { color: #b54845; font-size: 13px; }
.personnel-access-success { color: #23745d; font-size: 13px; }
.personnel-access-state { color: var(--ui-text-secondary, #71809e); padding: 18px 0; }
.personnel-access-list { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 18px; max-height: 380px; overflow-y: auto; border-top: 1px solid #e3eaf8; border-bottom: 1px solid #e3eaf8; }
.personnel-access-item { display: flex; align-items: flex-start; gap: 10px; padding: 11px 4px; margin: 0; border-bottom: 1px solid #eff2f8; font-weight: 400; cursor: pointer; }
.personnel-access-item input { flex: none; margin-top: 4px; }
.personnel-access-item span { min-width: 0; overflow-wrap: anywhere; }
.personnel-access-actions { display: flex; justify-content: flex-end; margin-top: 18px; }
@media (max-width: 620px) { .personnel-access-heading { align-items: flex-start; flex-direction: column; } .personnel-access-list { grid-template-columns: 1fr; } }
</style>
